import { useCallback, useEffect, useRef, useState } from 'react';
import { getVfdConfiguration } from '../services/vfdConfigurationService';
import vfdAgentClient from '../services/vfdAgentClient';

const money = (value) => Number(value || 0).toFixed(2);
const amountLine = (label, value, currency, columns = 20) => {
  const width = Math.max(8, Number(columns) || 20);
  const suffix = `${money(value)} ${currency || ''}`.trim();
  if (suffix.length >= width) return suffix.slice(-width);
  const clippedLabel = String(label || '').slice(0, Math.max(0, width - suffix.length - 1));
  return `${clippedLabel}${' '.repeat(width - clippedLabel.length - suffix.length)}${suffix}`;
};
export default function useVfdDisplay({ pointOfSaleId, cart, total, businessName = 'MATJARPOS' }) {
  const [status, setStatus] = useState({ state: 'disabled', error: null });
  const [configuration, setConfiguration] = useState(null);
  const configRef = useRef(null);
  const previousRef = useRef([]);
  const timerRef = useRef(null);
  const lastItemRef = useRef(null);
  const commandQueueRef = useRef(Promise.resolve());
  const logRef = useRef(0);
  const completedRef = useRef(false);
  const businessNameRef = useRef(businessName);
  businessNameRef.current = businessName;
  const idleNameRef = useRef(null);
  const showEmptyCart = useCallback((config) => {
    idleNameRef.current = businessNameRef.current;
    return vfdAgentClient.welcome(config, 'BIENVENUE', businessNameRef.current || 'MATJARPOS');
  }, []);

  const safely = useCallback((operation) => {
    const config = configRef.current;
    if (!config?.enabled) return Promise.resolve(null);
    const command = commandQueueRef.current.then(async () => {
    try { const result = await operation(config); setStatus({ state: result?.connected === false ? 'disconnected' : 'connected', error: result?.lastError || null }); return result; }
    catch (error) { setStatus({ state: 'unavailable', error: error?.response?.data?.error || error.message }); if (Date.now() - logRef.current > 30000) { console.warn('VFD agent unavailable:', error.message); logRef.current = Date.now(); } return null; }
    });
    commandQueueRef.current = command;
    return command;
  }, []);

  const showTotal = useCallback(() => safely((c) => {
    const item = lastItemRef.current;
    const label = item?.articleNom || item?.articleCode || 'ARTICLE';
    return vfdAgentClient.display(c, {
      line1: item?.prixUnitaireTTC == null ? label
        : amountLine(label, item.prixUnitaireTTC, c.currency, c.columns),
      line2: amountLine('TOTAL', total, c.currency, c.columns), returnToIdle: false,
    });
  }), [safely, total]);

  useEffect(() => {
    if (!pointOfSaleId) return undefined;
    let active = true;
    getVfdConfiguration(pointOfSaleId).then(async ({ data }) => {
      if (!active) return; configRef.current = data;
      if (!data.enabled) { setStatus({ state: 'disabled', error: null }); return; }
      idleNameRef.current = businessNameRef.current;
      await safely(async (c) => { await vfdAgentClient.connect(c); return showEmptyCart(c); });
      if (active) setConfiguration(data);
    }).catch((error) => active && setStatus({ state: 'configuration-error', error: error.message }));
    return () => { active = false; clearTimeout(timerRef.current); };
  }, [pointOfSaleId, safely, showEmptyCart]);

  useEffect(() => {
    const c = configRef.current; if (!c?.enabled || c !== configuration) return;
    clearTimeout(timerRef.current);
    if (!cart?.length) {
      const hadItems = previousRef.current.length > 0;
      previousRef.current = [];
      lastItemRef.current = null;
      if (!completedRef.current && (hadItems || idleNameRef.current !== businessName)) safely(showEmptyCart);
      return;
    }
    completedRef.current = false;
    const changed = [...cart].reverse().find((item) => {
      const old = previousRef.current.find((x) => String(x.articleId) === String(item.articleId));
      return !old || ['quantite', 'montantTTC', 'prixUnitaireTTC', 'remise', 'tauxTVA', 'articleNom', 'articleCode']
        .some((field) => old[field] !== item[field]);
    });
    previousRef.current = cart.map((item) => ({ ...item }));
    lastItemRef.current = changed || cart.find((item) => String(item.articleId) === String(lastItemRef.current?.articleId)) || cart[cart.length - 1];
    timerRef.current = setTimeout(showTotal, 180);
    return () => clearTimeout(timerRef.current);
  }, [cart, configuration, safely, showTotal, showEmptyCart, businessName]);

  const saleCompleted = useCallback(() => { clearTimeout(timerRef.current); completedRef.current = true; return safely((c) => vfdAgentClient.display(c, { line1:c.thankYouLine1 || 'MERCI',line2:c.thankYouLine2 || 'AU REVOIR',durationMs:(c.thankYouDurationSeconds || 7)*1000,returnToIdle:true })); }, [safely]);
  const saleCancelled = useCallback(() => { clearTimeout(timerRef.current);completedRef.current = false;return safely(showEmptyCart); }, [safely, showEmptyCart]);
  return { status, saleCompleted, saleCancelled };
}
